import React, { useState, useEffect } from "react";
import moment from "moment";
import axios from "axios";
import { Link } from "react-router-dom";
import { getCookie } from "../../services/DataRequestService";
import { useMetrics } from "../../contexts/MatricsContext";
import AuthService from "../../services/AuthService";
import { convertServerTimeToLocal } from "../../Utils/helper";
import Loader from "../common/Loader";
import { FaTimesCircle, FaFileAlt, FaClock, FaCheckCircle, FaUsers } from "react-icons/fa";
import PageHeader from "../common/PageHeader";
import DeleteWorkflowBar from "./DeleteWorkflowBar";


function KpiCard({ Icon, title, value, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (!onClick) return;
        if (e.key === "Enter" || e.key === " ") onClick();
      }}
      style={{
        ...pageStyles.kpiItem,
        ...(hovered ? pageStyles.kpiItemHover : {}),
        cursor: onClick ? "pointer" : "default",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={pageStyles.kpiIconWrap}>
        <Icon style={pageStyles.kpiIcon} />
      </div>
      <div style={pageStyles.kpiContent}>
        <h4 style={pageStyles.kpiItemH4}>{title}</h4>
        <p style={pageStyles.kpiValue}>{value}</p>
      </div>
    </div>
  );
}


const { REACT_APP_API_BASE_URL } = process.env;

const CACHE_KEY = "collaboration_portal_workflow_cache";
const POLL_INTERVAL = 10000;

function CollaborationPortal() {
  const [workFlowdata, setWorkFlowData] = useState([]);
  const [masterWorkFlowdata, setMasterWorkFlowdata] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 6;
  const { metrics } = useMetrics();

  const [modelStatusFilter, setModelStatusFilter] = useState("all");
  const [approvalStatusFilter, setApprovalStatusFilter] = useState("all");
  const [filteredFullList, setFilteredFullList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedKpi, setSelectedKpi] = useState(null);
  const [showKpiPopup, setShowKpiPopup] = useState(false);

  const [isPolling, setIsPolling] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const normalizeResponse = (response) => {
    let data = Array.isArray(response?.data?.data) ? [...response.data.data] : [];
    data.reverse();
    return data;
  };

  const areWorkflowListsEqual = (oldList, newList) => {
    return JSON.stringify(oldList) === JSON.stringify(newList);
  };

  const updateWorkflowState = (data) => {
    setWorkFlowData(data);
    setMasterWorkFlowdata(data);
    setFilteredFullList(data);
    setLastUpdated(new Date());
  };

  const loadCachedData = () => {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (!cached) return false;

      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed)) {
        updateWorkflowState(parsed);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error reading workflow cache:", error);
      return false;
    }
  };

  const saveCachedData = (data) => {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error("Error saving workflow cache:", error);
    }
  };

  const getData = async (options = { silent: false }) => {
    const { silent } = options;

    if (!silent) {
      setIsLoading(true);
    } else {
      setIsPolling(true);
    }

    try {
      const url = `${REACT_APP_API_BASE_URL}/get_workflows?user_id=${getCookie("sub")}`;
      const response = await axios.get(url, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          access_token: AuthService.getAccessToken(),
        },
      });

      const normalizedData = normalizeResponse(response);

      const cached = sessionStorage.getItem(CACHE_KEY);
      let cachedData = [];

      if (cached) {
        try {
          cachedData = JSON.parse(cached);
        } catch (e) {
          cachedData = [];
        }
      }

      const hasChanged = !areWorkflowListsEqual(cachedData, normalizedData);

      if (hasChanged) {
        updateWorkflowState(normalizedData);
        saveCachedData(normalizedData);
      }
    } catch (error) {
      console.error("Error fetching workflows:", error.message);
    } finally {
      if (!silent) {
        setIsLoading(false);
      } else {
        setIsPolling(false);
      }
    }
  };

  useEffect(() => {
    const hasCache = loadCachedData();

    if (!hasCache) {
      getData({ silent: false });
    } else {
      getData({ silent: true });
    }

    const interval = setInterval(() => {
      getData({ silent: true });
    }, POLL_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  const reset = () => {
    setWorkFlowData(masterWorkFlowdata);
    setModelStatusFilter("all");
    setApprovalStatusFilter("all");
    setCurrentPage(1);
    const input = document.getElementById("workflowName");
    if (input) input.value = "";
  };

  const toggleSelectOne = (workflowId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(workflowId)) {
        next.delete(workflowId);
      } else {
        next.add(workflowId);
      }
      return next;
    });
  };

  const selectedWorkflows = masterWorkFlowdata.filter((w) =>
    selectedIds.has(w.workflow_id)
  );

  const handleWorkflowsDeleted = (deletedIds) => {
    const deletedSet = new Set(deletedIds);
    const nextData = masterWorkFlowdata.filter(
      (w) => !deletedSet.has(w.workflow_id)
    );
    updateWorkflowState(nextData);
    saveCachedData(nextData);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      deletedIds.forEach((id) => next.delete(id));
      return next;
    });
  };

  const getAwaitingReview = () => {
    return (metrics?.notifications ?? []).filter(
      (n) =>
        n.reviewed_get_fp === false &&
        n.task_name === "consensus_plans_to_finalize"
    ).length;
  };

  const collabMatrics = [
    { label: "Outstanding Requests", value: metrics?.outstanding_requests || 0, Icon: FaFileAlt },
    { label: "Awaiting Your Review", value: getAwaitingReview(), Icon: FaClock },
    { label: "Completed This Week", value: metrics?.completed_this_week || 0, Icon: FaCheckCircle },
    { label: "Teams Involved", value: metrics?.teams_involved + 1 || 0, Icon: FaUsers },
  ];

  useEffect(() => {
    let data = [...workFlowdata].sort((a, b) => {
      const da = a.process_date ? new Date(a.process_date) : 0;
      const db = b.process_date ? new Date(b.process_date) : 0;
      return db - da;
    });

    if (modelStatusFilter !== "all") {
      data = data.filter(
        (w) => w.status?.toLowerCase() === modelStatusFilter
      );
    }

    if (approvalStatusFilter !== "all") {
      data = data.filter(
        (w) => w.approval_status?.toLowerCase() === approvalStatusFilter
      );
    }

    setFilteredFullList(data);
    setCurrentPage((prev) => {
      const total = Math.max(1, Math.ceil(data.length / rowsPerPage));
      return prev > total ? total : prev;
    });
  }, [workFlowdata, modelStatusFilter, approvalStatusFilter]);

  const totalPages = Math.ceil(filteredFullList.length / rowsPerPage) || 1;
  const currentData = filteredFullList.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const areAllVisibleSelected =
    currentData.length > 0 &&
    currentData.every((w) => selectedIds.has(w.workflow_id));

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (areAllVisibleSelected) {
        currentData.forEach((w) => next.delete(w.workflow_id));
      } else {
        currentData.forEach((w) => next.add(w.workflow_id));
      }
      return next;
    });
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getVisiblePages = (currentPage, totalPages) => {
    const PAGES_TO_SHOW = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + 4);

    if (endPage - startPage + 1 < PAGES_TO_SHOW) {
      startPage = Math.max(1, endPage - 4);
    }

    return Array.from(
      { length: endPage - startPage + 1 },
      (_, i) => startPage + i
    );
  };

  if (isLoading && workFlowdata.length === 0) {
    return (
      <center className="flex flex-col items-center justify-center" style={{ height: "100vh" }}>
        <Loader size={"large"} />
      </center>
    );
  }

  return (
    <>
      <div style={pageStyles.wrapper}>
        <div style={pageStyles.card}>
          <PageHeader
            title="Collaboration Portal"
            subtitle="Monitor and manage all your workbench workflows here."
          />

          <section style={pageStyles.kpiGrid} className="de-collab-kpi-grid">
            {collabMatrics.map((item, idx) => (
              <KpiCard
                key={idx}
                Icon={item.Icon}
                title={item.label}
                value={item.value}
                onClick={() => {
                  setSelectedKpi(item);
                  setShowKpiPopup(true);
                }}
              />
            ))}
          </section>

          <div style={pageStyles.filterBar}>
            <input
              id="workflowName"
              placeholder="Search by workflow name..."
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const value = e.target.value.toLowerCase();
                  if (value) {
                    const newData = masterWorkFlowdata.filter((w) =>
                      w.workflow_name.toLowerCase().includes(value)
                    );
                    setWorkFlowData(newData);
                    setCurrentPage(1);
                  } else {
                    reset();
                  }
                }
              }}
              style={pageStyles.searchInput}
            />

            <div style={pageStyles.filterActions}>
              <button
                type="button"
                onClick={() => {
                  setModelStatusFilter((prev) => {
                    const next =
                      prev === "processing"
                        ? "completed"
                        : prev === "completed"
                        ? "all"
                        : "processing";
                    setCurrentPage(1);
                    return next;
                  });
                }}
                style={{
                  ...pageStyles.filterBtn,
                  backgroundColor:
                    modelStatusFilter === "completed"
                      ? "#368E60"
                      : modelStatusFilter === "processing"
                      ? "#E06639"
                      : "#6a7d8c",
                }}
              >
                {modelStatusFilter === "completed"
                  ? "Model Status Completed"
                  : modelStatusFilter === "processing"
                  ? "Model Status Processing"
                  : "Model Status All"}
              </button>

              <button
                type="button"
                onClick={() => {
                  setApprovalStatusFilter((prev) => {
                    const next =
                      prev === "pending"
                        ? "approved"
                        : prev === "approved"
                        ? "all"
                        : "pending";
                    setCurrentPage(1);
                    return next;
                  });
                }}
                style={{
                  ...pageStyles.filterBtn,
                  backgroundColor:
                    approvalStatusFilter === "approved"
                      ? "#368E60"
                      : approvalStatusFilter === "pending"
                      ? "#6a7d8c"
                      : "#6b7280",
                }}
              >
                {approvalStatusFilter === "approved"
                  ? "APPROVAL STATUS Approved"
                  : approvalStatusFilter === "pending"
                  ? "APPROVAL STATUS Pending"
                  : "APPROVAL STATUS All"}
              </button>

              <button type="button" onClick={reset} style={pageStyles.resetBtn}>
                Reset Filter
              </button>

              <DeleteWorkflowBar
                selectedWorkflows={selectedWorkflows}
                onDeleted={handleWorkflowsDeleted}
              />
            </div>
          </div>

          <div style={pageStyles.tableWrap}>
            <div style={pageStyles.tableScroll}>
            <table style={pageStyles.table}>
              <thead>
                <tr>
                  <th style={{ ...pageStyles.th, width: 44 }}>
                    <input
                      type="checkbox"
                      checked={areAllVisibleSelected}
                      onChange={toggleSelectAllVisible}
                      aria-label="Select all workflows on this page"
                    />
                  </th>
                  <th style={pageStyles.th}>Workflow Name</th>
                  <th style={pageStyles.th}>Description</th>
                  <th style={pageStyles.th}>Granularity</th>
                  <th style={pageStyles.th}>Process Time</th>
                  <th style={pageStyles.th}>Process Date</th>
                  <th style={pageStyles.th}>Model Status</th>
                  <th style={pageStyles.th}>Approval</th>
                  <th style={pageStyles.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {currentData.length > 0 ? (
                  currentData.map((elem, index) => (
                    <tr key={elem.workflow_id || index} style={pageStyles.tr}>
                      <td style={pageStyles.tdCenter}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(elem.workflow_id)}
                          onChange={() => toggleSelectOne(elem.workflow_id)}
                          aria-label={`Select ${elem.workflow_name}`}
                        />
                      </td>
                      <td style={pageStyles.tdBold}>{elem.workflow_name}</td>
                      <td style={pageStyles.tdDesc}>{elem.workflow_description || "-"}</td>
                      <td style={pageStyles.td}>{elem.granularity}</td>
                      <td style={pageStyles.td}>
                        {convertServerTimeToLocal(elem.process_date) || "-"}
                      </td>
                      <td style={pageStyles.td}>
                        {elem?.process_date?.split("T")[0] || "-"}
                      </td>
                      <td style={pageStyles.tdCenter}>
                        <span
                          style={{
                            ...pageStyles.badge,
                            backgroundColor:
                              elem.status?.toLowerCase() === "completed"
                                ? "#368E60"
                                : elem.status?.toLowerCase() === "processing"
                                  ? "#E06639"
                                  : "#973848",
                          }}
                        >
                          {elem.status}
                        </span>
                      </td>
                      <td style={pageStyles.tdCenter}>
                        <span
                          style={{
                            ...pageStyles.badge,
                            backgroundColor:
                              elem.approval_status?.toLowerCase() === "approved"
                                ? "#368E60"
                                : "#6a7d8c",
                          }}
                        >
                          {elem.approval_status}
                        </span>
                      </td>
                      <td style={pageStyles.tdCenter}>
                        {elem?.status?.toLowerCase() === "completed" ? (
                          <Link
                            to={`/wf/comparison`}
                            state={{ workflow: elem }}
                            style={pageStyles.detailsLink}
                          >
                            View Workbench
                          </Link>
                        ) : (
                          <span style={pageStyles.emptyCell}>-</span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" style={pageStyles.emptyRow}>
                      No workflows found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>
          </div>

          <div
            style={{
              marginTop: "12px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "10px",
              fontFamily: "Poppins, sans-serif",
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                color: "#475569",
              }}
            >
              Data auto-refreshes every 10 seconds.
            </p>

            <p
              style={{
                margin: 0,
                fontSize: "14px",
                color: isPolling ? "#E06639" : "#64748B",
                fontWeight: 500,
              }}
            >
              {isPolling
                ? "Checking for updates..."
                : lastUpdated
                ? `Last updated: ${lastUpdated.toLocaleTimeString()}`
                : "Waiting for first sync..."}
            </p>
          </div>

          <div className="flex justify-center mt-12" style={{ fontFamily: "Poppins, sans-serif" }}>
            <nav>
              <ul className="flex items-center space-x-4 text-2xl">
                <li>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-8 py-4 border-2 border-[#032B4E] rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#E06639] hover:text-white transition-all duration-300 transform hover:scale-105 font-bold text-2xl"
                    style={{ fontWeight: 500 }}
                  >
                    Previous
                  </button>
                </li>

                {getVisiblePages(currentPage, totalPages).map((pageNum) => (
                  <li key={pageNum}>
                    <button
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-6 py-4 rounded-xl border-2 border-[#032B4E] font-bold transition-all duration-300 transform hover:scale-105 text-2xl ${
                        currentPage === pageNum
                          ? "bg-[#E06639] text-white shadow-lg"
                          : "hover:bg-[#E06639] hover:text-white text-[#032B4E]"
                      }`}
                      style={{ fontWeight: 500 }}
                    >
                      {pageNum}
                    </button>
                  </li>
                ))}

                <li>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-8 py-4 border-2 border-[#032B4E] rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#E06639] hover:text-white transition-all duration-300 transform hover:scale-105 font-bold text-2xl"
                    style={{ fontWeight: 500 }}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>

      {showKpiPopup && selectedKpi && (
        <div
          onClick={() => setShowKpiPopup(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(2, 26, 48, 0.65)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "20px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="collab-kpi-title"
            style={{
              width: "100%",
              maxWidth: "800px",
              background: "#ffffff",
              borderRadius: "20px",
              padding: "28px 28px 24px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
              border: "1px solid #e6eef4",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: "16px",
                marginBottom: "16px",
              }}
            >
              <div>
                <h2
                  id="collab-kpi-title"
                  style={{
                    margin: 0,
                    fontSize: "28px",
                    fontWeight: 600,
                    color: "#0F172A",
                    fontFamily: "Poppins, sans-serif",
                  }}
                >
                  {selectedKpi.label}
                </h2>
                <p
                  style={{
                    margin: "8px 0 0",
                    fontSize: "15px",
                    color: "#475569",
                    lineHeight: 1.7,
                    fontFamily: "Poppins, sans-serif",
                  }}
                >
                  {selectedKpi.details}
                </p>
              </div>

              <button
                onClick={() => setShowKpiPopup(false)}
                style={pageStyles.modalCloseBtn}
                aria-label="Close"
              >
                <FaTimesCircle />
              </button>
            </div>

            <div
              style={{
                marginTop: "18px",
                padding: "16px 18px",
                borderRadius: "14px",
                background: "#F8FAFC",
                border: "1px solid #CBD5E1",
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  color: "#334155",
                  lineHeight: 1.7,
                  fontFamily: "Poppins, sans-serif",
                }}
              >
                <strong>Current value:</strong> {selectedKpi.value}
              </p>
            </div>
          </div>
        </div>
      )}
      <style>
        {`
          @media (max-width: 991px) {
            .de-collab-kpi-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }
          }
          @media (max-width: 767px) {
            .de-collab-kpi-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>

    </>
  );
}

export default CollaborationPortal;

const pageStyles = {
  wrapper: {
    width: "100%",
    margin: 0,
    padding: 0,
    boxSizing: "border-box",
    background: "transparent",
  },
  modalCloseBtn: {
    background: "transparent",
    border: "none",
    color: "#6B7280",
    fontSize: "28px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    lineHeight: 1,
  },
  card: {
    width: "100%",
    background: "transparent",
    padding: "8px 4px 20px",
    borderRadius: 0,
    boxShadow: "none",
    border: "none",
    overflow: "visible",
  },
  inner: {
    width: "100%",
    margin: "0 auto",
    color: "#0c3c54",
    display: "flex",
    flexDirection: "column",
  },
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 14,
    width: "100%",
    marginBottom: 28,
  },
  kpiItem: {
    background: "linear-gradient(135deg, #021a30 0%, #032B4E 55%, #032B4E 100%)",
    borderRadius: 12,
    padding: "16px 18px",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    color: "#ffffff",
    boxShadow: "0 4px 14px rgba(3, 43, 78, 0.14)",
    display: "flex",
    alignItems: "center",
    gap: 14,
    minHeight: 96,
    boxSizing: "border-box",
    cursor: "default",
  },
  kpiIconWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  kpiIcon: { fontSize: 44, color: "#E06639" },
  kpiContent: { minWidth: 0, flex: 1 },
  kpiItemH4: {
    fontSize: 13,
    fontWeight: 600,
    color: "rgba(255,255,255,0.82)",
    margin: "0 0 4px",
    lineHeight: 1.3,
    fontFamily: "Poppins, sans-serif",
  },
  kpiValue: {
    fontSize: 24,
    fontWeight: 700,
    color: "#ffffff",
    margin: 0,
    lineHeight: 1.1,
    fontFamily: "Poppins, sans-serif",
  },
  kpiItemHover: {
    transform: "translateY(-3px)",
    boxShadow: "0 10px 24px rgba(3, 43, 78, 0.22)",
  },
  filterBar: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    marginBottom: 20,
  },
  searchInput: {
    flex: "1 1 280px",
    maxWidth: 360,
    padding: "10px 14px",
    fontSize: 14,
    border: "1px solid #e6eef4",
    borderRadius: 8,
    background: "#fff",
    color: "#0c3c54",
    boxSizing: "border-box",
    fontFamily: "Poppins, sans-serif",
    outline: "none",
  },
  filterActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    alignItems: "center",
  },
  filterBtn: {
    padding: "8px 16px",
    borderRadius: 8,
    border: "none",
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "Poppins, sans-serif",
    cursor: "pointer",
  },
  resetBtn: {
    padding: "8px 16px",
    borderRadius: 8,
    border: "none",
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "Poppins, sans-serif",
    cursor: "pointer",
    background: "#E06639",
    boxShadow: "0 3px 10px rgba(224, 102, 57, 0.25)",
  },
  tableWrap: {
    width: "100%",
    borderRadius: 12,
    border: "3px solid #032B4E",
    boxShadow: "0 3px 12px rgba(12, 60, 84, 0.06)",
    background: "#032B4E",
    overflow: "hidden",
  },
  tableScroll: {
    width: "100%",
    overflowX: "auto",
    background: "#fff",
  },
  table: {
    width: "100%",
    minWidth: 980,
    borderCollapse: "collapse",
    tableLayout: "auto",
    fontFamily: "Poppins, sans-serif",
  },
  th: {
    padding: "12px 16px",
    textAlign: "center",
    fontSize: 13,
    fontWeight: 700,
    color: "#ffffff",
    background: "#032B4E",
    border: "1px solid #032B4E",
    whiteSpace: "nowrap",
  },
  tr: {
    background: "#fff",
    transition: "background 0.15s ease",
  },
  td: {
    padding: "12px 16px",
    textAlign: "center",
    fontSize: 13,
    fontWeight: 500,
    color: "#6a7d8c",
    border: "1px solid #e2eaf0",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  tdBold: {
    padding: "12px 16px",
    textAlign: "center",
    fontSize: 13,
    fontWeight: 600,
    color: "#0c3c54",
    border: "1px solid #e2eaf0",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  tdDesc: {
    padding: "12px 16px",
    textAlign: "left",
    fontSize: 13,
    fontWeight: 500,
    color: "#6a7d8c",
    border: "1px solid #e2eaf0",
    verticalAlign: "middle",
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    cursor: "pointer",
  },
  tdDescExpanded: {
    whiteSpace: "normal",
    overflow: "visible",
    textOverflow: "unset",
    wordBreak: "break-word",
    verticalAlign: "top",
    background: "#f8fbfd",
  },
  tdCenter: {
    padding: "12px 16px",
    textAlign: "center",
    border: "1px solid #e2eaf0",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  badge: {
    display: "inline-block",
    minWidth: 100,
    padding: "6px 12px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    color: "#fff",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    fontFamily: "Poppins, sans-serif",
  },
  emptyCell: {
    color: "#9ca3af",
    fontSize: 13,
  },
  emptyRow: {
    textAlign: "center",
    padding: "40px 16px",
    fontSize: 14,
    color: "#6a7d8c",
    fontFamily: "Poppins, sans-serif",
  },
  detailsLink: {
    display: "inline-block",
    padding: "7px 14px",
    borderRadius: 8,
    background: "#E06639",
    color: "#ffffff",
    fontWeight: 700,
    fontSize: 12,
    textDecoration: "none",
    fontFamily: "Poppins, sans-serif",
    boxShadow: "0 2px 8px rgba(224, 102, 57, 0.28)",
    border: "none",
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    transition: "background 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease",
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 24,
  },
  pageBtn: {
    padding: "8px 14px",
    borderRadius: 8,
    border: "1px solid #e6eef4",
    background: "#fff",
    color: "#0c3c54",
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "Poppins, sans-serif",
    cursor: "pointer",
  },
  pageBtnActive: {
    background: "#E06639",
    color: "#fff",
    borderColor: "#E06639",
    boxShadow: "0 3px 10px rgba(224, 102, 57, 0.25)",
  },
};
